import React, { useEffect, useMemo, useRef, useState } from "react";
import "./CheckoutModal.css";
import { useCart } from "../context/CartContext";
import SmallGTLogo from "../assets/brand/SmallGTLogo.svg";
import TokenBadge from "../assets/icons/TokenBadge.svg";
import OrderGotToken from "../assets/icons/OrderGotToken.svg";
import { getOrderOnBackend, getOrderTokenEstimate } from "../services/razorpay";
import { fetchVisitRewardStatus } from "../services/visitReward";
import { APP_DOWNLOAD_URL, VISIT_REWARD_POPUP_ENABLED } from "../config/payments";
import VisitRewardScreen from "./VisitRewardScreen";
import {
  completeRegistration,
  formatVerifiedPhone,
  getDisplayName,
  sendOtp,
  verifyOtp,
} from "../services/menuAuth";
import { getTableNumberFromUrl } from "../utils/tableNumber";

const ORDER_TYPES = [
  { value: "dine-in", label: "Dine In" },
  { value: "takeaway", label: "Takeaway" },
];

const EMPTY_CUSTOMER = {
  name: "",
  phone: "",
  displayPhone: "",
  orderType: "dine-in",
  tableNumber: "",
};

const TABLE_MIN = 1;
const TABLE_MAX = 100;
const TABLE_NOTE = "Please stay at this table. If you move, tell a staff member so we deliver to the right place.";
const OTP_LENGTH = 6;
const DEFAULT_BIRTH_DATE = "2000-01-01";
const EMPTY_PROFILE = {
  fullName: "",
  dateOfBirth: DEFAULT_BIRTH_DATE,
};

const getUrlTableNumber = () => getTableNumberFromUrl();

const normalizeManualTableNumber = (value) => {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  const tableNumber = Number(digits);
  if (!Number.isFinite(tableNumber) || tableNumber < TABLE_MIN) return "";
  return String(Math.min(tableNumber, TABLE_MAX));
};

const lastTenDigits = (value) => String(value || "").replace(/\D/g, "").slice(-10);

const splitFullName = (fullName) => {
  const parts = String(fullName || "").trim().split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
};

const todayDateInput = () => new Date().toISOString().slice(0, 10);

const getMinBirthDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 100);
  return date.toISOString().slice(0, 10);
};

const getMaxBirthDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 13);
  return date.toISOString().slice(0, 10);
};

function getOtpErrorMessage(error) {
  if (error?.code === "otp-cooldown") return error.message || "Please wait before requesting another OTP.";
  if (error?.code === "otp-expired") return "OTP expired. Please request a new code.";
  if (error?.code === "otp-attempts-exceeded") return "Too many incorrect attempts. Please request a new code.";
  if (error?.code === "invalid-otp") return "Incorrect OTP entered.";
  if (error?.code === "sms-provider-failed") return "Could not send OTP. Please try again.";
  return error?.message || "Something went wrong. Please try again.";
}

export default function CheckoutModal({ open, onClose, onSuccess }) {
  const {
    items,
    grandTotal,
    itemCount,
    placeOrder,
    cafeInfo,
    cartStatus,
    orderContext,
    ristaInfo,
    paymentInfo,
    startNewCartAfterSuccessfulOrder,
    reconcileBackendOrderStatus,
  } = useCart();

  const [customer, setCustomer] = useState(EMPTY_CUSTOMER);
  const [stage, setStage] = useState("phone");
  const [phoneInput, setPhoneInput] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [otp, setOtp] = useState("");
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState({ ...EMPTY_PROFILE });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [backendOrder, setBackendOrder] = useState(null);
  const [formError, setFormError] = useState("");
  const [tokenEstimate, setTokenEstimate] = useState({ status: "idle", totalTokens: null });
  const [visitReward, setVisitReward] = useState(null);
  const [rewardStepOpen, setRewardStepOpen] = useState(false);
  const visitRewardFetchedOrderIdRef = useRef("");
  const sheetRef = useRef(null);
  const preserveCheckoutOnNextOpenRef = useRef(false);
  const finalizedReceiptOrderIdRef = useRef("");
  const lockedTableNumber = getUrlTableNumber();
  const hasLockedTable = Boolean(lockedTableNumber);
  const tableInputRequired = customer.orderType === "dine-in" && !hasLockedTable;
  const tableInputMissing =
    tableInputRequired && !normalizeManualTableNumber(customer.tableNumber);

  const maxBirthDate = useMemo(getMaxBirthDate, []);
  const minBirthDate = useMemo(getMinBirthDate, []);
  const tokenEstimateItems = useMemo(
    () =>
      items.map((item) => ({
        skuCode: item.skuCode,
        itemId: item.itemId,
        name: item.name,
        shortName: item.shortName,
        longName: item.longName,
        categoryName: item.categoryName,
        unitPrice: item.unitPrice,
        quantity: item.quantity,
      })),
    [items]
  );
  const estimatedTokens = tokenEstimate.totalTokens;
  const hasTokenEstimate = tokenEstimate.status === "ready";

  useEffect(() => {
    if (open) {
      if (preserveCheckoutOnNextOpenRef.current) {
        preserveCheckoutOnNextOpenRef.current = false;
        setSubmitting(false);
        setResult(null);
        setFormError("");
        return;
      }

      setResult(null);
      setBackendOrder(null);
      setVisitReward(null);
      setRewardStepOpen(false);
      setFormError("");
      setSubmitting(false);
      setStage("phone");
      setPhoneInput("");
      setChallenge(null);
      setOtp("");
      setAuthUser(null);
      setProfile({ ...EMPTY_PROFILE });
      setCustomer((prev) => ({
        ...EMPTY_CUSTOMER,
        orderType: prev.orderType || "dine-in",
        tableNumber: lockedTableNumber || "",
      }));
    }
  }, [open, orderContext?.tableNumber, lockedTableNumber, hasLockedTable]);

  useEffect(() => {
    if (!open) {
      setTokenEstimate({ status: "idle", totalTokens: null });
      return undefined;
    }

    if (tokenEstimateItems.length === 0) {
      setTokenEstimate({ status: "ready", totalTokens: 0 });
      return undefined;
    }

    const controller = new AbortController();
    setTokenEstimate({ status: "loading", totalTokens: null });

    getOrderTokenEstimate(tokenEstimateItems, { signal: controller.signal })
      .then((estimate) => {
        if (!controller.signal.aborted) {
          setTokenEstimate({ status: "ready", totalTokens: estimate.totalTokens });
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setTokenEstimate({ status: "unavailable", totalTokens: null });
        }
      });

    return () => controller.abort();
  }, [open, tokenEstimateItems]);

  useEffect(() => {
    if (!open || !result?.success || !result?.orderId) return undefined;
    if (cartStatus === "rista_failed") return undefined;
    if (finalizedReceiptOrderIdRef.current === result.orderId) return undefined;

    let cancelled = false;
    let timer = null;

    const pollOrder = async () => {
      try {
        const order = await getOrderOnBackend(result.orderId);
        if (cancelled || !order) return;

        const backendRistaStatus = order?.rista?.status;
        if (backendRistaStatus === "rista_success" || backendRistaStatus === "rista_failed") {
          // Reconciliation updates the cart context, which recreates this
          // effect. Mark the receipt complete first so it can never start a
          // new poll once the Cafe Order ID is available.
          finalizedReceiptOrderIdRef.current = result.orderId;
          if (!cancelled) setBackendOrder(order);
          // The receipt is sourced from the backend order. Updating the local
          // cart is useful for the next session, but must never hold up the
          // customer-facing result (mobile Firestore writes can be delayed).
          reconcileBackendOrderStatus(order).catch((error) => {
            console.warn("[checkout] local cart reconciliation failed:", error);
          });
          return;
        } else {
          setBackendOrder(order);
        }
      } catch (error) {
        console.warn("[checkout] backend order status poll failed:", error);
      }

      if (!cancelled) {
        timer = window.setTimeout(pollOrder, 2000);
      }
    };

    timer = window.setTimeout(pollOrder, 1500);

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [open, result, cartStatus, reconcileBackendOrderStatus]);

  useEffect(() => {
    if (!open) return undefined;
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  // Once the cafe has accepted the order, ask whether it is the customer's 1st-4th
  // visit. If so, a reward screen follows the receipt's Done button. Fails silently.
  const orderConfirmedForReward =
    cartStatus !== "rista_failed" &&
    (cartStatus === "rista_success" || backendOrder?.rista?.status === "rista_success");
  const rewardOrderId = result?.success ? result.orderId : "";
  const rewardPhone = customer.phone;
  useEffect(() => {
    if (!VISIT_REWARD_POPUP_ENABLED || !open || !orderConfirmedForReward) return undefined;
    if (!rewardOrderId || !rewardPhone) return undefined;
    if (visitRewardFetchedOrderIdRef.current === rewardOrderId) return undefined;
    visitRewardFetchedOrderIdRef.current = rewardOrderId;

    const controller = new AbortController();
    fetchVisitRewardStatus(rewardOrderId, rewardPhone, { signal: controller.signal }).then(
      (projection) => {
        if (!controller.signal.aborted && projection) setVisitReward(projection);
      }
    );
    return () => controller.abort();
  }, [open, orderConfirmedForReward, rewardOrderId, rewardPhone]);

  if (!open) return null;

  const updateCustomer = (field, value) => {
    setCustomer((prev) => ({ ...prev, [field]: value }));
    setFormError("");
  };

  const updateOrderType = (orderType) => {
    setCustomer((prev) => ({
      ...prev,
      orderType,
      tableNumber:
        hasLockedTable
          ? lockedTableNumber
          : orderType === "dine-in"
          ? prev.tableNumber || ""
          : prev.tableNumber,
    }));
    setFormError("");
  };

  const updateTableNumber = (value) => {
    if (hasLockedTable) return;
    updateCustomer("tableNumber", normalizeManualTableNumber(value));
  };

  const lockVerifiedCustomer = ({ user, phoneNumber }) => {
    const displayPhone = formatVerifiedPhone(phoneNumber || user?.phoneNumber || "");
    const paymentPhone = lastTenDigits(displayPhone);
    const name = getDisplayName(user);
    setCustomer((prev) => ({
      ...prev,
      name,
      phone: paymentPhone,
      displayPhone,
      orderType: prev.orderType || "dine-in",
      tableNumber: hasLockedTable ? lockedTableNumber : prev.tableNumber,
    }));
  };

  const handleSendOtp = async () => {
    const digits = phoneInput.replace(/\D/g, "");
    if (digits.length !== 10) {
      setFormError("Please enter a valid 10-digit phone number");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      const nextChallenge = await sendOtp(digits);
      setChallenge(nextChallenge);
      setOtp("");
      setStage("otp");
    } catch (error) {
      setFormError(getOtpErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!challenge || otp.length !== OTP_LENGTH) {
      setFormError("Please enter the 6-digit OTP");
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      const verified = await verifyOtp(challenge, otp);
      const user = verified.user || {
        uid: verified.uid,
        phoneNumber: verified.phoneNumber,
        isRegistered: false,
      };
      setAuthUser(user);

      if (user?.isRegistered === true) {
        lockVerifiedCustomer({ user, phoneNumber: verified.phoneNumber });
        setStage("payment");
        return;
      }

      setProfile((prev) => ({
        ...prev,
        fullName: getDisplayName(user) === "Got Tea Customer" ? "" : getDisplayName(user),
        dateOfBirth: prev.dateOfBirth || DEFAULT_BIRTH_DATE,
      }));
      setCustomer((prev) => ({
        ...prev,
        phone: lastTenDigits(verified.phoneNumber || challenge.phoneNumber),
        displayPhone: formatVerifiedPhone(verified.phoneNumber || challenge.phoneNumber),
        name: "",
        isRegisteredAppUser: false,
      }));
      setStage("payment");
    } catch (error) {
      setFormError(getOtpErrorMessage(error));
      setOtp("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setOtp("");
    setSubmitting(true);
    setFormError("");
    try {
      const nextChallenge = await sendOtp(phoneInput);
      setChallenge(nextChallenge);
    } catch (error) {
      setFormError(getOtpErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const validateProfileDetails = () => {
    const { firstName, lastName } = splitFullName(profile.fullName);

    if (!firstName) {
      return { error: "Please enter your name" };
    }
    if (!profile.dateOfBirth) {
      return { error: "Please enter your date of birth" };
    }
    if (profile.dateOfBirth > maxBirthDate || profile.dateOfBirth < minBirthDate || profile.dateOfBirth > todayDateInput()) {
      return { error: "Please enter a valid date of birth" };
    }

    return { firstName, lastName };
  };

  const shouldCompleteProfileBeforePayment =
    Boolean(authUser) && authUser?.isRegistered !== true && customer.isRegisteredAppUser !== true;

  const completeProfileBeforePayment = async () => {
    const profileDetails = validateProfileDetails();
    if (profileDetails.error) {
      throw new Error(profileDetails.error);
    }

    const user = await completeRegistration({
      firstName: profileDetails.firstName,
      lastName: profileDetails.lastName,
      dateOfBirth: profile.dateOfBirth,
    });
    const registeredName = getDisplayName(user);
    const checkoutName = registeredName === "Got Tea Customer" ? profile.fullName.trim() : registeredName;

    setAuthUser(user);
    lockVerifiedCustomer({ user, phoneNumber: user.phoneNumber || customer.displayPhone });
    setCustomer((prev) => ({
      ...prev,
      name: checkoutName,
      registrationStatus: "registered",
      isRegisteredAppUser: true,
    }));

    return { user, checkoutName };
  };

  const validatePaymentDetails = () => {
    if (!authUser) return "Please verify your phone number before payment";
    if (!customer.phone || customer.phone.length !== 10) return "Verified phone number is missing";
    if (shouldCompleteProfileBeforePayment) {
      const profileDetails = validateProfileDetails();
      if (profileDetails.error) return profileDetails.error;
    } else if (!customer.name.trim()) {
      return "Please enter your name";
    }
    if (customer.orderType === "dine-in") {
      const tableNumber = hasLockedTable ? lockedTableNumber : normalizeManualTableNumber(customer.tableNumber);
      if (!tableNumber) return "Please enter your table number for dine-in orders";
      if (!hasLockedTable) {
        const numericTable = Number(tableNumber);
        if (numericTable < TABLE_MIN || numericTable > TABLE_MAX) {
          return `Please enter a table number between ${TABLE_MIN} and ${TABLE_MAX}`;
        }
      }
    }
    if (items.length === 0) return "Your cart is empty";
    return "";
  };

  const handlePlaceOrder = async () => {
    const error = validatePaymentDetails();
    if (error) {
      setFormError(error);
      return;
    }

    setSubmitting(true);
    setFormError("");

    let checkoutUser = authUser;
    let checkoutName = customer.name.trim();
    let registrationStatus = customer.registrationStatus || "registered";
    let isRegisteredAppUser = customer.isRegisteredAppUser === true || authUser?.isRegistered === true;

    try {
      if (shouldCompleteProfileBeforePayment) {
        const registration = await completeProfileBeforePayment();
        checkoutUser = registration.user;
        checkoutName = registration.checkoutName;
        registrationStatus = "registered";
        isRegisteredAppUser = true;
      }
    } catch (profileError) {
      setSubmitting(false);
      setFormError(profileError?.message || "Failed to complete registration. Please try again.");
      return;
    }

    const outcome = await placeOrder({
      name: checkoutName,
      phone: customer.phone,
      orderType: customer.orderType,
      tableNumber:
        hasLockedTable
          ? lockedTableNumber
          : customer.orderType === "dine-in"
          ? normalizeManualTableNumber(customer.tableNumber)
          : "",
      notes: String(orderContext?.notes || "").trim(),
      appUserId: checkoutUser?.uid || "",
      isRegisteredAppUser,
      registrationStatus,
      estimatedMissedTokens: isRegisteredAppUser || !hasTokenEstimate ? 0 : estimatedTokens,
    });

    setSubmitting(false);
    setResult(outcome);

    if (!outcome.success && outcome.code === "sold_out_items") {
      setFormError(outcome.error || "Some items in your cart are now sold out.");
      preserveCheckoutOnNextOpenRef.current = true;
      onClose();
      return;
    }

    if (!outcome.success && outcome.code === "availability_check_failed") {
      setFormError(outcome.error || "Could not confirm item availability. Please try again.");
      return;
    }

  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  const handleDone = () => {
    if (visitReward && !rewardStepOpen) {
      setRewardStepOpen(true);
      return undefined;
    }
    return finishOrder();
  };

  const finishOrder = async () => {
    const backendRistaStatus = backendOrder?.rista?.status;
    const effectiveCartStatus =
      cartStatus === "rista_success" || cartStatus === "rista_failed"
        ? cartStatus
        : backendRistaStatus || cartStatus;
    const shouldStartNewCart = effectiveCartStatus === "rista_success";
    setSubmitting(true);
    setFormError("");
    try {
      if (shouldStartNewCart) {
        await startNewCartAfterSuccessfulOrder();
      }
    } finally {
      setSubmitting(false);
    }

    if (shouldStartNewCart && onSuccess) {
      onSuccess(result);
      return;
    }

    onClose();
  };

  const backendRistaStatus = backendOrder?.rista?.status;
  const effectiveCartStatus =
    cartStatus === "rista_success" || cartStatus === "rista_failed"
      ? cartStatus
      : backendRistaStatus || cartStatus;
  const effectiveRistaInfo =
    cartStatus === "rista_success" || cartStatus === "rista_failed"
      ? ristaInfo
      : backendOrder?.rista || ristaInfo;
  const effectivePaymentInfo = paymentInfo || backendOrder?.payment || null;
  const showRistaSuccess = effectiveCartStatus === "rista_success";
  const showRistaFailure = effectiveCartStatus === "rista_failed";
  const showPending = result?.success && !showRistaSuccess && !showRistaFailure;
  const showSuccess = showRistaSuccess;
  const showFailure = result && !result.success && !result.dismissed;
  const showEstimatedTokenAward =
    showSuccess && hasTokenEstimate && Number(estimatedTokens) > 0;
  const ristaReceiptId = effectiveRistaInfo?.invoiceNumber || effectiveRistaInfo?.orderNumber;
  const receiptId = showSuccess && ristaReceiptId
    ? ristaReceiptId
    : result?.orderId
    ? `#${result.orderId.slice(-8).toUpperCase()}`
    : "";
  const receiptIdLabel = showSuccess && ristaReceiptId ? "Cafe Order ID" : "Order ID";
  const paidAmount = Math.round(
    Number(effectivePaymentInfo?.amountPaise ? effectivePaymentInfo.amountPaise / 100 : grandTotal)
  );
  const showRewardStep = rewardStepOpen && showSuccess && Boolean(visitReward);
  const headerTitle = showRewardStep
    ? "Your Reward"
    : showSuccess
    ? "Order Confirmed"
    : showPending
    ? "Payment Received"
    : stage === "phone"
    ? "Verify Phone"
    : stage === "otp"
    ? "Enter OTP"
    : "Confirm Order";

  return (
    <div className="checkout-overlay" onClick={handleOverlayClick}>
      <div className="checkout-sheet" ref={sheetRef}>
        <div className="checkout-header">
          <div className="checkout-brand-row">
            <img src={SmallGTLogo} alt="" className="checkout-brand-logo" />
            <div>
              <h3>{headerTitle}</h3>
              <p>
                {showRewardStep
                  ? "Unlocks in the Got Tea app"
                  : showSuccess
                  ? "Your order was sent to the cafe"
                  : showPending
                  ? "Waiting for cafe confirmation"
                  : `${itemCount} item${itemCount === 1 ? "" : "s"} · ₹${Math.round(grandTotal)}`}
              </p>
            </div>
          </div>
          <button
            className="checkout-close"
            onClick={showRewardStep ? finishOrder : onClose}
            disabled={submitting}
            aria-label="Close checkout"
          >
            ×
          </button>
        </div>

        {showRewardStep ? (
          <VisitRewardScreen
            reward={visitReward.reward}
            unlockAt={visitReward.unlockAt}
            serverNow={visitReward.serverNow}
            isRegisteredAppUser={
              authUser?.isRegistered === true || customer.isRegisteredAppUser === true
            }
            appUrl={APP_DOWNLOAD_URL}
          />
        ) : showSuccess || showPending || showRistaFailure ? (
          <div className="checkout-success">
            <div className={`checkout-success-icon ${showPending ? "pending" : showRistaFailure ? "failed" : ""}`}>
              {showPending ? "…" : showRistaFailure ? "!" : "✓"}
            </div>
            <p className="checkout-success-title">
              {showPending
                ? "Payment received"
                : showRistaFailure
                ? "Order needs help"
                : `Thank you, ${customer.name.split(" ")[0] || "there"}!`}
            </p>
            <p className="checkout-success-sub">
              {showPending
                ? "Your payment is verified. We are confirming the order with the cafe."
                : showRistaFailure
                ? effectiveRistaInfo?.error || "Payment succeeded, but Rista order creation failed. Please contact the counter."
                : (
                    <>
                      Your order has been placed at{" "}
                      <strong>{cafeInfo.branchName || "Got Tea"}</strong>.
                    </>
                  )}
            </p>
            {receiptId && (
              <div className="checkout-success-receipt">
                <span>{receiptIdLabel}</span>
                <strong>{receiptId}</strong>
              </div>
            )}
            {orderContext?.tableLabel && (
              <div className="checkout-success-receipt">
                <span>Table</span>
                <strong>{orderContext.tableLabel}</strong>
              </div>
            )}
            <div className="checkout-success-receipt">
              <span>Amount Paid</span>
              <strong>₹{paidAmount}</strong>
            </div>
            {showEstimatedTokenAward && (
              <div className="checkout-success-receipt checkout-success-tokens">
                <span><img src={OrderGotToken} alt="" />Got Tokens Earned</span>
                <strong>+{estimatedTokens}</strong>
              </div>
            )}
            {!showPending && (
              <button className="checkout-primary" onClick={handleDone} disabled={submitting}>
                Done
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="checkout-body">
              {showFailure && (
                <div className="checkout-alert">
                  {result?.error || "Payment could not be completed. Please try again."}
                </div>
              )}
              {formError && <div className="checkout-alert checkout-alert-warn">{formError}</div>}

              {stage === "phone" && (
                <div className="checkout-step">
                  <div className="checkout-token-hero">
                    <img src={TokenBadge} alt="" className="checkout-token-icon" />
                    <div className="checkout-token-copy">
                      <strong>
                        {hasTokenEstimate
                          ? `Earn ${estimatedTokens} Got Tokens`
                          : tokenEstimate.status === "loading"
                          ? "Calculating Got Tokens…"
                          : "Got Token estimate unavailable"}
                      </strong>
                      <p>
                        Verify your phone number before payment.
                        {hasTokenEstimate && Number(estimatedTokens) === 0 && (
                          <> Tokens are only earned on Food, Desserts and Drinks.</>
                        )}
                      </p>
                    </div>
                  </div>
                  <p className="checkout-step-title">Enter your mobile number</p>
                  <p className="checkout-step-subtitle">We will send a quick OTP before you pay.</p>
                  <label className="checkout-field">
                    <span>Phone number</span>
                    <div className="checkout-phone-row">
                      <span className="checkout-phone-prefix">+91</span>
                      <input
                        type="tel"
                        value={phoneInput}
                        onChange={(e) => {
                          setPhoneInput(e.target.value.replace(/\D/g, "").slice(0, 10));
                          setFormError("");
                        }}
                        placeholder="00000 00000"
                        autoComplete="tel"
                        inputMode="numeric"
                        maxLength={10}
                        disabled={submitting}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleSendOtp();
                        }}
                      />
                    </div>
                  </label>
                </div>
              )}

              {stage === "otp" && (
                <div className="checkout-step">
                  <p className="checkout-step-title">Enter the OTP</p>
                  <p className="checkout-step-subtitle">Sent to {challenge?.phoneNumber || "+91" + phoneInput}</p>
                  <label className="checkout-field">
                    <span>6-digit code</span>
                    <input
                      className="checkout-otp-input"
                      type="tel"
                      value={otp}
                      onChange={(e) => {
                        setOtp(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH));
                        setFormError("");
                      }}
                      placeholder="000000"
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      maxLength={OTP_LENGTH}
                      disabled={submitting}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleVerifyOtp();
                      }}
                    />
                  </label>
                  <button className="checkout-link-button" onClick={handleResendOtp} disabled={submitting}>
                    Resend OTP
                  </button>
                </div>
              )}

              {stage === "payment" && (
                <div className="checkout-step">
                  {shouldCompleteProfileBeforePayment && (
                    <div className="checkout-token-card checkout-token-card-highlight">
                      <img src={TokenBadge} alt="" className="checkout-token-icon small" />
                      <div className="checkout-token-copy">
                        <span>Complete sign up to earn</span>
                        <strong>
                          {hasTokenEstimate
                            ? `${estimatedTokens} Got Tokens`
                            : tokenEstimate.status === "loading"
                            ? "Calculating Got Tokens…"
                            : "Got Token estimate unavailable"}
                        </strong>
                      </div>
                    </div>
                  )}
                  <div className="checkout-identity-card">
                    <span>Verified phone</span>
                    <strong>{customer.displayPhone}</strong>
                  </div>
                  {shouldCompleteProfileBeforePayment ? (
                    <>
                      <label className="checkout-field">
                        <span>Full name</span>
                        <input
                          type="text"
                          value={profile.fullName}
                          onChange={(e) => {
                            setProfile((prev) => ({ ...prev, fullName: e.target.value }));
                            setFormError("");
                          }}
                          placeholder="Your name"
                          autoComplete="name"
                          disabled={submitting}
                        />
                      </label>
                      <label className="checkout-field">
                        <span>Date of birth</span>
                        <input
                          type="date"
                          value={profile.dateOfBirth}
                          min={minBirthDate}
                          max={maxBirthDate}
                          onChange={(e) => {
                            setProfile((prev) => ({ ...prev, dateOfBirth: e.target.value }));
                            setFormError("");
                          }}
                          disabled={submitting}
                        />
                      </label>
                    </>
                  ) : (
                    <label className="checkout-field">
                      <span>Name</span>
                      <input
                        type="text"
                        value={customer.name}
                        onChange={(e) => updateCustomer("name", e.target.value)}
                        placeholder="Your name"
                        autoComplete="name"
                        disabled={submitting || authUser?.isRegistered === true}
                      />
                    </label>
                  )}

                  <div className="checkout-field-row">
                    <label className="checkout-field">
                      <span>Order Type</span>
                      <select
                        value={customer.orderType}
                        onChange={(e) => updateOrderType(e.target.value)}
                        disabled={submitting}
                      >
                        {ORDER_TYPES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </label>

                    {hasLockedTable ? (
                      <div className="checkout-identity-card checkout-identity-card-stacked">
                        <div className="checkout-identity-card-head">
                          <span>Table</span>
                          <strong>Table {lockedTableNumber}</strong>
                        </div>
                        <p className="checkout-table-note">{TABLE_NOTE}</p>
                      </div>
                    ) : customer.orderType === "dine-in" ? (
                      <label
                        className={`checkout-field${
                          tableInputMissing ? " checkout-field-error" : ""
                        }`}
                      >
                        <span>Table</span>
                        <input
                          type="number"
                          value={customer.tableNumber}
                          onChange={(e) => updateTableNumber(e.target.value)}
                          placeholder="1-100"
                          inputMode="numeric"
                          min={TABLE_MIN}
                          max={TABLE_MAX}
                          disabled={submitting}
                        />
                        {tableInputMissing ? (
                          <p className="checkout-table-note checkout-table-note-error">
                            Enter the table number printed on your QR code to continue.
                          </p>
                        ) : (
                          <p className="checkout-table-note">{TABLE_NOTE}</p>
                        )}
                      </label>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

            <div className="checkout-footer">
              {stage === "phone" && (
                <button className="checkout-primary" onClick={handleSendOtp} disabled={submitting || items.length === 0}>
                  {submitting ? "Sending..." : "Send OTP"}
                </button>
              )}
              {stage === "otp" && (
                <button className="checkout-primary" onClick={handleVerifyOtp} disabled={submitting || otp.length !== OTP_LENGTH}>
                  {submitting ? "Verifying..." : "Verify OTP"}
                </button>
              )}
              {stage === "payment" && (
                <button className="checkout-primary" onClick={handlePlaceOrder} disabled={submitting || items.length === 0 || tableInputMissing}>
                  {submitting ? "Processing..." : `Pay ₹${Math.round(grandTotal)}`}
                </button>
              )}
              <p className="checkout-secure-note">Secured by Razorpay</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
